import React, { useEffect, useState } from "react";
import Sidebar from "../components/Sidebar";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { FaCopy, FaArrowLeft } from "react-icons/fa";

const AddAdmissionForm = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [colleges, setColleges] = useState([]);
  const [courses, setCourses] = useState([]);
  const [shareableUrl, setShareableUrl] = useState("");
  const [showUrl, setShowUrl] = useState(false);
  const [copied, setCopied] = useState(false);

  const [formData, setFormData] = useState({
    studentName: '',
    mobile: '',
    email: '',
    fatherName: '',
    fatherMobile: '',
    selectedCourses: [],
    tenthPercentage: '',
    twelfthPercentage: '',
    graduationPercentage: '',
    itiDiploma: '',
    collegeName: '',
    address: '',
    aadharCard: '',
    collegeId: ''
  });

  const [errors, setErrors] = useState({});

  useEffect(() => {
    fetchColleges();
  }, []);

  const fetchColleges = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_BASE_URL}/all-colleges`, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `${localStorage.getItem("token")}`,
        },
      });
      setColleges(response.data.data);
    } catch (error) {
      console.error("Error fetching colleges:", error);
    }
  };

  const handleCollegeChange = (e) => {
    const collegeId = e.target.value;
    setFormData({
      ...formData,
      collegeId,
      selectedCourses: []
    });
    
    if (collegeId) {
      const selectedCollege = colleges.find(college => college._id === collegeId);
      if (selectedCollege && selectedCollege.courses) {
        setCourses(selectedCollege.courses);
      } else {
        setCourses([]);
      }
      
      setFormData(prev => ({
        ...prev,
        collegeId,
        collegeName: selectedCollege?.name || '',
        selectedCourses: []
      }));
    } else {
      setCourses([]);
    }
  };

  const handleCourseChange = (courseId) => {
    const isSelected = formData.selectedCourses.includes(courseId);
    if (isSelected) {
      setFormData({
        ...formData,
        selectedCourses: formData.selectedCourses.filter(id => id !== courseId)
      });
    } else {
      setFormData({
        ...formData,
        selectedCourses: [...formData.selectedCourses, courseId]
      });
    }
  };

  const validateForm = () => {
    const newErrors = {};

   if (!formData.collegeId) newErrors.collegeId = 'College is required';
    if (!formData.selectedCourses || formData.selectedCourses.length === 0) newErrors.selectedCourses = 'Course selection is required';

    // Optional percentage validations - only validate if values are provided
    if (formData.tenthPercentage) {
      const percentage = parseFloat(formData.tenthPercentage);
      if (isNaN(percentage) || percentage < 0 || percentage > 100) 
        newErrors.tenthPercentage = 'Enter valid percentage (0-100)';
    }

    if (formData.twelfthPercentage) {
      const percentage = parseFloat(formData.twelfthPercentage);
      if (isNaN(percentage) || percentage < 0 || percentage > 100) 
        newErrors.twelfthPercentage = 'Enter valid percentage (0-100)';
    }

    if (formData.graduationPercentage) {
      const percentage = parseFloat(formData.graduationPercentage);
      if (isNaN(percentage) || percentage < 0 || percentage > 100) 
        newErrors.graduationPercentage = 'Enter valid percentage (0-100)';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;

    setLoading(true);
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_BASE_URL}/create-admission-form`,
        { admissionData: formData },
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `${localStorage.getItem("token")}`,
          },
        }
      );

      setShareableUrl(response.data.data.shareableUrl);
      setShowUrl(true);
    } catch (error) {
      console.error("Error creating admission form:", error);
      alert(error.response?.data?.message || "Failed to create admission form");
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(shareableUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      console.error("Failed to copy:", error);
    }
  };

  const handleRedirect = () => {
    navigate("/admission-manager");
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
    
    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: ''
      });
    }
  };

  if (showUrl) {
    return (
      <div className="min-h-screen flex">
        <Sidebar />
        <div className="flex-1 p-6 bg-gray-50">
          <div className="max-w-2xl mx-auto">
            <div className="bg-white p-8 rounded-lg shadow-md">
              <h2 className="text-2xl font-bold text-green-600 mb-4">Admission Form Created Successfully!</h2>
              
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Shareable URL
                </label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={shareableUrl}
                    readOnly
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-md bg-gray-50"
                  />
                  <button
                    onClick={copyToClipboard}
                    className="px-4 py-2 bg-blue text-white rounded-md hover:bg-blue-600 flex items-center gap-2"
                  >
                    <FaCopy />
                    {copied ? "Copied!" : "Copy"}
                  </button>
                </div>
              </div>

              <p className="text-gray-600 mb-6">
                Share this URL with students to fill out the admission form.
              </p>

              <button
                onClick={handleRedirect}
                className="w-full bg-green-500 text-white px-4 py-2 rounded-lg hover:bg-green-600"
              >
                Go to Admission Manager
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex bg-gray-100">
      <Sidebar />
      <div className="flex-1 p-6">
        <h1 className="text-2xl font-bold mb-6">Create Admission Form</h1>
        <form onSubmit={handleSubmit}>
          {/* Student Name */}
          <div className="mb-4">
            <label className="block font-bold mb-2">Student Name</label>
            <input
              type="text"
              name="studentName"
              value={formData.studentName}
              onChange={handleInputChange}
              className={`border border-gray-300 rounded-md px-4 py-2 w-full ${errors.studentName ? 'border-red-500' : ''}`}
              placeholder="Enter student name"
            />
            {errors.studentName && <p className="text-red-500">{errors.studentName}</p>}
          </div>

          {/* Email */}
          <div className="mb-4">
            <label className="block font-bold mb-2">Email</label>
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleInputChange}
              className={`border border-gray-300 rounded-md px-4 py-2 w-full ${errors.email ? 'border-red-500' : ''}`}
              placeholder="Enter email"
            />
            {errors.email && <p className="text-red-500">{errors.email}</p>}
          </div>

          {/* Mobile */}
          <div className="mb-4">
            <label className="block font-bold mb-2">Mobile</label>
            <input
              type="text"
              name="mobile"
              value={formData.mobile}
              onChange={handleInputChange}
              className={`border border-gray-300 rounded-md px-4 py-2 w-full ${errors.mobile ? 'border-red-500' : ''}`}
              maxLength="10"
              placeholder="Enter mobile number"
            />
            {errors.mobile && <p className="text-red-500">{errors.mobile}</p>}
          </div>

          {/* Father Name */}
          <div className="mb-4">
            <label className="block font-bold mb-2">Father Name</label>
            <input
              type="text"
              name="fatherName"
              value={formData.fatherName}
              onChange={handleInputChange}
              className={`border border-gray-300 rounded-md px-4 py-2 w-full ${errors.fatherName ? 'border-red-500' : ''}`}
              placeholder="Enter father name"
            />
            {errors.fatherName && <p className="text-red-500">{errors.fatherName}</p>}
          </div>

          {/* Father Mobile */}
          <div className="mb-4">
            <label className="block font-bold mb-2">Father Mobile</label>
            <input
              type="text"
              name="fatherMobile"
              value={formData.fatherMobile}
              onChange={handleInputChange}
              className={`border border-gray-300 rounded-md px-4 py-2 w-full ${errors.fatherMobile ? 'border-red-500' : ''}`}
              maxLength="10"
              placeholder="Enter father mobile number"
            />
            {errors.fatherMobile && <p className="text-red-500">{errors.fatherMobile}</p>}
          </div>

          {/* College */}
          <div className="mb-4">
            <label className="block font-bold mb-2">College *</label>
            <select
              name="collegeId"
              value={formData.collegeId}
              onChange={handleCollegeChange}
              className={`border border-gray-300 rounded-md px-4 py-2 w-full ${errors.collegeId ? 'border-red-500' : ''}`}
            >
              <option value="">Select College</option>
              {colleges.map((college) => (
                <option key={college._id} value={college._id}>
                  {college.name}
                </option>
              ))}
            </select>
            {errors.collegeId && <p className="text-red-500">{errors.collegeId}</p>}
          </div>

          {/* Courses */}
          {courses.length > 0 && (
            <div className="mb-4">
              <label className="block font-bold mb-2">Courses</label>
              <div className="border border-gray-300 rounded-md p-3 h-40 overflow-y-auto bg-gray-50">
                <div className="space-y-2">
                  {courses.map((course) => (
                    <label key={course._id} className="flex items-center cursor-pointer hover:bg-gray-100 p-2 rounded">
                      <input
                        type="checkbox"
                        checked={formData.selectedCourses.includes(course._id)}
                        onChange={() => handleCourseChange(course._id)}
                        className="mr-2"
                      />
                      {course.name}
                    </label>
                  ))}
                </div>
              </div>
              {errors.selectedCourses && <p className="text-red-500">{errors.selectedCourses}</p>}
            </div>
          )}

          {/* 10th Percentage */}
          <div className="mb-4">
            <label className="block font-bold mb-2">10th Percentage</label>
            <input
              type="number"
              name="tenthPercentage"
              value={formData.tenthPercentage}
              onChange={handleInputChange}
              className={`border border-gray-300 rounded-md px-4 py-2 w-full ${errors.tenthPercentage ? 'border-red-500' : ''}`}
              min="0"
              max="100"
              step="0.1"
              placeholder="Enter 10th percentage"
            />
            {errors.tenthPercentage && <p className="text-red-500">{errors.tenthPercentage}</p>}
          </div>

          {/* 12th Percentage */}
          <div className="mb-4">
            <label className="block font-bold mb-2">12th Percentage</label>
            <input
              type="number"
              name="twelfthPercentage"
              value={formData.twelfthPercentage}
              onChange={handleInputChange}
              className={`border border-gray-300 rounded-md px-4 py-2 w-full ${errors.twelfthPercentage ? 'border-red-500' : ''}`}
              min="0"
              max="100"
              step="0.1"
              placeholder="Enter 12th percentage"
            />
            {errors.twelfthPercentage && <p className="text-red-500">{errors.twelfthPercentage}</p>}
          </div>

          {/* Graduation Percentage */}
          <div className="mb-4">
            <label className="block font-bold mb-2">Graduation Percentage</label>
            <input
              type="number"
              name="graduationPercentage"
              value={formData.graduationPercentage}
              onChange={handleInputChange}
              className="border border-gray-300 rounded-md px-4 py-2 w-full"
              min="0"
              max="100"
              step="0.1"
              placeholder="Enter graduation percentage"
            />
          </div>

          {/* ITI/Diploma */}
          <div className="mb-4">
            <label className="block font-bold mb-2">ITI/Diploma</label>
            <input
              type="text"
              name="itiDiploma"
              value={formData.itiDiploma}
              onChange={handleInputChange}
              className="border border-gray-300 rounded-md px-4 py-2 w-full"
              placeholder="Enter ITI/Diploma details"
            />
          </div>

          {/* Aadhar Card */}
          <div className="mb-4">
            <label className="block font-bold mb-2">Aadhar Card</label>
            <input
              type="text"
              name="aadharCard"
              value={formData.aadharCard}
              onChange={handleInputChange}
              className={`border border-gray-300 rounded-md px-4 py-2 w-full ${errors.aadharCard ? 'border-red-500' : ''}`}
              maxLength="12"
              placeholder="Enter Aadhar card number"
            />
            {errors.aadharCard && <p className="text-red-500">{errors.aadharCard}</p>}
          </div>

          {/* Address */}
          <div className="mb-4">
            <label className="block font-bold mb-2">Address</label>
            <textarea
              name="address"
              value={formData.address}
              onChange={handleInputChange}
              className={`border border-gray-300 rounded-md px-4 py-2 w-full ${errors.address ? 'border-red-500' : ''}`}
              rows="3"
              placeholder="Enter address"
            />
            {errors.address && <p className="text-red-500">{errors.address}</p>}
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            disabled={loading}
            className="bg-blue text-white px-6 py-2 rounded-md shadow-md hover:bg-blue-600"
          >
            {loading ? "Creating..." : "Create Admission Form"}
          </button>
        </form>
      </div>
    </div>
  );
};

export default AddAdmissionForm;
